// src/pages/admin/Building/ViewBuilding.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaBuilding,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaPhone,
  FaEnvelope,
  FaGlobe,
  FaUsers,
  FaBed,
  FaStar,
  FaCheckCircle,
  FaImage,
  FaEdit,
  FaPrint,
  FaDownload,
  FaSpinner,
  FaCalendarAlt,
  FaInfoCircle,
  FaTag,
  FaCity,
  FaLocationArrow,
  FaExpand,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import { MdClose } from 'react-icons/md';
import { successToast, errorToast } from '../../Utils/toast';
import { useGetAdminBuildingQuery } from '../../redux/services/buildingService/building.Services';

import './ViewBuilding.css';

function ViewBuilding() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useGetAdminBuildingQuery(id);

  const [building, setBuilding] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(0);

  useEffect(() => {
    if (data?.data) {
      setBuilding(data.data);
      if (data.data.images && data.data.images.length > 0) {
        const primaryImage = data.data.images.find(img => img.is_primary) || data.data.images[0];
        setActiveImage(primaryImage);
        setZoomIndex(0);
      }
    }
  }, [data]);

  // Get type label
  const getTypeLabel = (type) => {
    const typeMap = {
      'samaj_bhawan': 'Samaj Bhawan',
      'dharamshala': 'Dharamshala',
      'community_hall': 'Community Hall',
      'marriage_hall': 'Marriage Hall',
    };
    return typeMap[type] || type || 'N/A';
  };

  // Get type badge color
  const getTypeBadgeColor = (type) => {
    const typeMap = {
      'samaj_bhawan': 'primary',
      'dharamshala': 'success',
      'community_hall': 'info',
      'marriage_hall': 'warning',
    };
    return typeMap[type] || 'secondary';
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusMap = {
      'pending': { class: 'badge bg-warning', label: 'Pending' },
      'approved': { class: 'badge bg-success', label: 'Approved' },
      'rejected': { class: 'badge bg-danger', label: 'Rejected' },
    };
    const statusInfo = statusMap[status] || statusMap['pending'];
    return <span className={statusInfo.class}>{statusInfo.label}</span>;
  };

  // Format price
  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Handle edit navigation
  const handleEdit = () => {
    navigate(`/admin/edit-building/${id}`);
  };

  // Open zoom modal
  const openZoom = (index) => {
    setZoomIndex(index);
    setIsZoomOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Close zoom modal
  const closeZoom = () => {
    setIsZoomOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Navigate zoom images
  const zoomPrev = () => {
    setZoomIndex((prev) => (prev > 0 ? prev - 1 : building.images.length - 1));
  };

  const zoomNext = () => {
    setZoomIndex((prev) => (prev < building.images.length - 1 ? prev + 1 : 0));
  };

  // Keyboard navigation for zoom
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isZoomOpen) return;
      if (e.key === 'Escape') closeZoom();
      if (e.key === 'ArrowLeft') zoomPrev();
      if (e.key === 'ArrowRight') zoomNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isZoomOpen]);

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <FaSpinner className="spinning fs-1 text-primary" />
        <h5 className="mt-3">Loading building details...</h5>
      </div>
    );
  }

  if (isError || !building) {
    return (
      <div className="text-center py-5">
        <div className="alert alert-danger">
          <h5>Error loading building</h5>
          <p>{error?.data?.message || 'Building not found'}</p>
          <button className="btn btn-primary" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const images = building.images || [];

  return (
    <div className="view-building-page">
      {/* HEADER */}
      <div className="view-building-header">
        <div className="header-left">
          <Link to="/admin/buildings" className="back-btn">
            <FaArrowLeft /> Back
          </Link>
          <h1 className="page-title">
            <FaBuilding className="title-icon" />
            {building.name}
          </h1>
        </div>
        <div className="header-right">
          <button className="btn-print" onClick={handlePrint}>
            <FaPrint /> Print
          </button>
          <button className="btn-edit" onClick={handleEdit}>
            <FaEdit /> Edit
          </button>
        </div>
      </div>

      {/* BREADCRUMB */}
      <div className="breadcrumb-bar">
        <Link to="/admin">Home</Link>
        <span className="separator">/</span>
        <Link to="/admin/buildings">Buildings</Link>
        <span className="separator">/</span>
        <span className="current">{building.name}</span>
      </div>

      {/* MAIN CONTENT */}
      <div className="view-building-content">
        {/* Status Badges */}
        <div className="status-badges">
          <span className={`type-badge type-${building.type}`}>
            {getTypeLabel(building.type)}
          </span>
          {getStatusBadge(building.status)}
          {building.is_featured && (
            <span className="featured-badge">
              <FaStar /> Featured
            </span>
          )}
          {building.is_active ? (
            <span className="active-badge">
              <FaCheckCircle /> Active
            </span>
          ) : (
            <span className="inactive-badge">Inactive</span>
          )}
        </div>

        {/* Image Gallery Section */}
        <div className="gallery-section">
          <div className="section-header">
            <h3>
              <FaImage className="section-icon" />
              Images Gallery
            </h3>
            <span className="image-count">{images.length} images</span>
          </div>

          {images.length > 0 ? (
            <div className="gallery-container">
              {/* Main Image */}
              <div className="main-image-wrapper">
                {activeImage ? (
                  <div className="main-image-container">
                    <img
                      src={activeImage.image_url}
                      alt={activeImage.caption || building.name}
                      className="main-image"
                    />
                    <button 
                      className="zoom-btn"
                      onClick={() => openZoom(images.findIndex(img => img.id === activeImage.id))}
                      title="Zoom Image"
                    >
                      <FaExpand />
                    </button>
                    {activeImage.is_primary && (
                      <span className="primary-badge">
                        <FaStar /> Primary
                      </span>
                    )}
                    {activeImage.caption && (
                      <div className="image-caption">{activeImage.caption}</div>
                    )}
                  </div>
                ) : (
                  <div className="main-image-placeholder">
                    <FaImage size={64} className="text-muted" />
                    <p>No image selected</p>
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              <div className="thumbnails-wrapper">
                <div className="thumbnails-grid">
                  {images.map((image, index) => (
                    <div
                      key={image.id}
                      className={`thumbnail-item ${activeImage?.id === image.id ? 'active' : ''}`}
                      onClick={() => setActiveImage(image)}
                    >
                      <img
                        src={image.image_url}
                        alt={image.caption || 'Building image'}
                        className="thumbnail-image"
                      />
                      {image.is_primary && (
                        <span className="thumbnail-badge">
                          <FaStar />
                        </span>
                      )}
                      <div className="thumbnail-overlay">
                        <FaExpand />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="no-images">
              <FaImage size={48} className="no-images-icon" />
              <p>No images available for this building</p>
            </div>
          )}
        </div>

        {/* Building Details */}
        <div className="details-grid">
          {/* Basic Information */}
          <div className="detail-card">
            <div className="card-header">
              <FaInfoCircle className="header-icon" />
              <h4>Basic Information</h4>
            </div>
            <div className="card-body">
              <div className="detail-row">
                <span className="detail-label">Building Name</span>
                <span className="detail-value fw-bold">{building.name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Slug</span>
                <span className="detail-value">{building.slug}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Type</span>
                <span className="detail-value">
                  <span className={`badge bg-${getTypeBadgeColor(building.type)}`}>
                    {getTypeLabel(building.type)}
                  </span>
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Views</span>
                <span className="detail-value">{building.view_count || 0}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Created</span>
                <span className="detail-value">{formatDate(building.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="detail-card">
            <div className="card-header">
              <FaMapMarkerAlt className="header-icon" />
              <h4>Location Information</h4>
            </div>
            <div className="card-body">
              <div className="detail-row">
                <span className="detail-label">Address</span>
                <span className="detail-value">{building.address || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">City</span>
                <span className="detail-value">{building.city || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">State</span>
                <span className="detail-value">{building.state || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Country</span>
                <span className="detail-value">{building.country || 'India'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Pincode</span>
                <span className="detail-value">{building.pincode || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="detail-card">
            <div className="card-header">
              <FaPhone className="header-icon" />
              <h4>Contact Information</h4>
            </div>
            <div className="card-body">
              <div className="detail-row">
                <span className="detail-label">Phone</span>
                <span className="detail-value">
                  {building.phone ? (
                    <a href={`tel:${building.phone}`} className="contact-link">
                      {building.phone}
                    </a>
                  ) : (
                    'N/A'
                  )}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email</span>
                <span className="detail-value">
                  {building.email ? (
                    <a href={`mailto:${building.email}`} className="contact-link">
                      {building.email}
                    </a>
                  ) : (
                    'N/A'
                  )}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Website</span>
                <span className="detail-value">
                  {building.website ? (
                    <a href={building.website} target="_blank" rel="noopener noreferrer" className="contact-link">
                      {building.website}
                    </a>
                  ) : (
                    'N/A'
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Building Details */}
          <div className="detail-card">
            <div className="card-header">
              <FaBuilding className="header-icon" />
              <h4>Building Details</h4>
            </div>
            <div className="card-body">
              <div className="detail-row">
                <span className="detail-label">Capacity</span>
                <span className="detail-value">
                  {building.capacity ? `${building.capacity} People` : 'N/A'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Rooms</span>
                <span className="detail-value">
                  {building.rooms ? `${building.rooms} Rooms` : 'N/A'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Facilities</span>
                <span className="detail-value">{building.facilities || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Pricing Information */}
          <div className="detail-card">
            <div className="card-header">
              <FaMoneyBillWave className="header-icon" />
              <h4>Pricing Information</h4>
            </div>
            <div className="card-body">
              <div className="detail-row">
                <span className="detail-label">Price Per Day</span>
                <span className="detail-value price-value">
                  {formatPrice(building.price_per_day)}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Security Deposit</span>
                <span className="detail-value">
                  {formatPrice(building.security_deposit)}
                </span>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="detail-card full-width">
            <div className="card-header">
              <FaInfoCircle className="header-icon" />
              <h4>Description</h4>
            </div>
            <div className="card-body">
              <div className="description-text">
                {building.description || 'No description available'}
              </div>
            </div>
          </div>

          {/* SEO Information */}
          <div className="detail-card full-width">
            <div className="card-header">
              <FaTag className="header-icon" />
              <h4>SEO / Meta Information</h4>
            </div>
            <div className="card-body">
              <div className="detail-row">
                <span className="detail-label">Meta Title</span>
                <span className="detail-value">{building.meta_title || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Meta Description</span>
                <span className="detail-value">{building.meta_description || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Meta Keywords</span>
                <span className="detail-value">{building.meta_keywords || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="detail-card full-width">
            <div className="card-header">
              <FaCalendarAlt className="header-icon" />
              <h4>Timestamps</h4>
            </div>
            <div className="card-body">
              <div className="detail-row">
                <span className="detail-label">Created At</span>
                <span className="detail-value">{formatDate(building.created_at)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Updated At</span>
                <span className="detail-value">{formatDate(building.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button className="action-btn btn-edit" onClick={handleEdit}>
            <FaEdit /> Edit Building
          </button>
          <button className="action-btn btn-print" onClick={handlePrint}>
            <FaPrint /> Print Details
          </button>
          <Link to="/admin/buildings" className="action-btn btn-back">
            <FaArrowLeft /> Back to List
          </Link>
        </div>
      </div>

      {/* ============================================= */}
      {/* IMAGE ZOOM MODAL */}
      {/* ============================================= */}
      {isZoomOpen && images.length > 0 && (
        <div className="zoom-modal" onClick={closeZoom}>
          <div className="zoom-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="zoom-close" onClick={closeZoom}>
              <FaTimes />
            </button>
            
            <div className="zoom-image-container">
              <img
                src={images[zoomIndex]?.image_url}
                alt={images[zoomIndex]?.caption || building.name}
                className="zoom-image"
              />
              {images[zoomIndex]?.is_primary && (
                <span className="zoom-primary-badge">
                  <FaStar /> Primary
                </span>
              )}
              {images[zoomIndex]?.caption && (
                <div className="zoom-caption">
                  {images[zoomIndex].caption}
                </div>
              )}
            </div>

            {images.length > 1 && (
              <>
                <button className="zoom-nav zoom-prev" onClick={zoomPrev}>
                  <FaChevronLeft />
                </button>
                <button className="zoom-nav zoom-next" onClick={zoomNext}>
                  <FaChevronRight />
                </button>
                <div className="zoom-counter">
                  {zoomIndex + 1} / {images.length}
                </div>
              </>
            )}

            <div className="zoom-thumbnails">
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className={`zoom-thumbnail ${index === zoomIndex ? 'active' : ''}`}
                  onClick={() => setZoomIndex(index)}
                >
                  <img
                    src={image.image_url}
                    alt={image.caption || 'Building image'}
                    className="zoom-thumbnail-img"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewBuilding;